import type { AgencyServedPortRef } from '@gat-ship/types'
import { shallowRef, ref } from 'vue'
import { getAgencyPortsRequest } from '../model/agency-port-api-calls'

// Module-level cache keyed by agency id. Unlike the country / company /
// currency ref sources, agency ports are agency-scoped — two pickers on the
// same screen
// may target different agencies, so each consumer gets its own reactive `ports`
// / `loadingPorts` refs while the network result is deduped through this shared
// cache.
const cache = new Map<string, AgencyServedPortRef[]>()

export function useSelectAgencyPortsDataSource() {
  const ports = shallowRef<AgencyServedPortRef[] | undefined>(undefined)
  const loadingPorts = ref(false)

  // Tracks the most recently requested agency so a slow in-flight response for
  // a previous agency can't clobber the current selection (last-write-wins).
  let requestedAgencyId: string | undefined

  async function fetchPorts(agencyId: string, force?: true) {
    requestedAgencyId = agencyId

    if (!agencyId) {
      ports.value = []
      return
    }

    const cached = cache.get(agencyId)
    if (cached && !force) {
      ports.value = cached
      return
    }

    loadingPorts.value = true
    try {
      const result = await getAgencyPortsRequest(agencyId)
      cache.set(agencyId, result)
      if (requestedAgencyId === agencyId) {
        ports.value = result
      }
    } finally {
      if (requestedAgencyId === agencyId) {
        loadingPorts.value = false
      }
    }
  }

  return {
    ports,
    loadingPorts,
    fetchPorts
  }
}
